import {useQuery, QueryFunctionContext, UseQueryResult} from '@tanstack/react-query';

import {ENDPOINTS} from 'constants/endpoints';
import {apiConfig} from 'libs/api/api';

type SubscriptionPlanType = {
    label: string;
    code: string;
    boxesPerEye: number;
    countOfMonths: number;
};
export type SubscriptionPlansType = SubscriptionPlanType[];
export type SubscriptionDropdownPlanType = {
    value: string;
    label: string;
    boxesPerEye: number;
    countOfMonths: number;
};

const SUBSCRIPTION_PLANS_QUERY_KEY = 'SUBSCRIPTION_PLANS_QUERY_KEY';

/**
 * Fetches plans by product id
 * @param {QueryFunctionContext} - query function parameters
 * @return {SubscriptionPlansType} - subscription plans
 */
const fetchPlans = async ({queryKey: [, id]}: QueryFunctionContext) => {
    try {
        const {data} = await apiConfig.instanceGusaBackend.request<SubscriptionPlansType>({
            baseURL: apiConfig.API_URL,
            method: 'get',
            url: `${ENDPOINTS.GET_CL_SUBSCRIPTION_PLANS({id: id as string})}`,
        });

        return data;
    } catch (error) {
        const [currentError] = error?.response?.data?.messages?.error || [
            {message: 'Something went wrong. Try again later.'},
        ];

        throw new Error(currentError.message);
    }
};

/**
 * Map subscription plans for the dropdown format
 * @param {SubscriptionPlansType} inputPlans - subscription plans
 * @return {object} - mapped subscription plans
 */
const mapPlans = (inputPlans: SubscriptionPlansType) =>
    inputPlans.map(inputPlan => ({
        value: inputPlan.code,
        ...inputPlan,
    }));

/**
 * Hook for fetching the subscription plans
 * @param {string | undefined} productId - product id
 * @returns {UseQueryResult} - hook
 */
export const useSubscriptionPlansRequest = (
    productId?: string,
): UseQueryResult<SubscriptionDropdownPlanType[]> =>
    useQuery({
        enabled: Boolean(productId),
        queryFn: fetchPlans,
        queryKey: [SUBSCRIPTION_PLANS_QUERY_KEY, productId],
        select: mapPlans,
        staleTime: 300000,
    });
